import { FootballGame } from '../types/games_api_types';
import React, { useState, useEffect, useCallback } from 'react';
import { GameV0 } from '@/types';
import classes from './BaseballSoftballGameControl.module.css';
import GameScorecard from '../game/GameScorecard';

type GameSide = 'home' | 'away';

export default function FootballGameControl({ uid }: { uid: string }) {
  const [game, setGame] = useState<FootballGame | null>(null);
  const [awayScores, setAwayScores] = useState<(number | null)[]>([]);
  const [homeScores, setHomeScores] = useState<(number | null)[]>([]);
  const [maxPeriods, setMaxPeriods] = useState<number>(4);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [period, setPeriod] = useState<number>(1);
  const [minutes, setMinutes] = useState<number>(15);
  const [seconds, setSeconds] = useState<number>(0);
  const [down, setDown] = useState<number | null>(1);
  const [distance, setDistance] = useState<number | null>(10);
  const [ballYardline, setBallYardline] = useState<number | null>(50);
  const [ballFieldSide, setBallFieldSide] = useState<GameSide | null>('home');
  const [possession, setPossession] = useState<GameSide>('home');
  const [awayTimeouts, setAwayTimeouts] = useState<number>(3);
  const [homeTimeouts, setHomeTimeouts] = useState<number>(3);

  useEffect(() => {
    const loadGame = async () => {
      const response = await fetch(`/api/v1/games/football/${uid}.json`);
      const gameData = await response.json();
      setGame(gameData);

      const maxLen = Math.max(
        gameData.away_score_array?.length || 0,
        gameData.home_score_array?.length || 0,
      );
      setAwayScores(gameData.away_score_array || []);
      setHomeScores(gameData.home_score_array || []);
      setMaxPeriods(Math.max(maxLen, gameData.base_period_count || 4));

      if (gameData.period !== undefined && gameData.period !== null)
        setPeriod(gameData.period);
      if (gameData.minutes !== undefined && gameData.minutes !== null)
        setMinutes(gameData.minutes);
      if (gameData.seconds !== undefined && gameData.seconds !== null)
        setSeconds(gameData.seconds);
      setDown(gameData.down ?? null);
      setDistance(gameData.distance ?? null);
      setBallYardline(gameData.ball_yardline ?? null);
      if (
        gameData.ball_field_side === 'home' ||
        gameData.ball_field_side === 'away'
      ) {
        setBallFieldSide(gameData.ball_field_side);
      } else {
        setBallFieldSide(null);
      }
      if (gameData.possession === 'home' || gameData.possession === 'away') {
        setPossession(gameData.possession);
      }
      if (
        gameData.away_team_timeouts !== undefined &&
        gameData.away_team_timeouts !== null
      )
        setAwayTimeouts(gameData.away_team_timeouts);
      if (
        gameData.home_team_timeouts !== undefined &&
        gameData.home_team_timeouts !== null
      )
        setHomeTimeouts(gameData.home_team_timeouts);
    };

    loadGame();
  }, [uid]);

  const startGame = useCallback(async () => {
    const response = await fetch(`/admin/football/games/${uid}/start.json`, {
      method: 'POST',
    });
    setGame(await response.json());
  }, [uid]);

  const handleScoreChange = (
    periodIndex: number,
    team: 'away' | 'home',
    value: string,
  ) => {
    const scores = team === 'away' ? [...awayScores] : [...homeScores];
    scores[periodIndex] = value === '' ? null : parseInt(value, 10);
    if (team === 'away') {
      setAwayScores(scores);
    } else {
      setHomeScores(scores);
    }
  };

  const addExtraPeriod = () => {
    setMaxPeriods(maxPeriods + 1);
  };

  const handleSubmitScores = async () => {
    setIsSubmitting(true);
    try {
      const paddedAwayScores = [...awayScores];
      const paddedHomeScores = [...homeScores];

      while (paddedAwayScores.length < maxPeriods) {
        paddedAwayScores.push(null);
      }
      while (paddedHomeScores.length < maxPeriods) {
        paddedHomeScores.push(null);
      }

      const response = await fetch(
        `/admin/football/games/${uid}/update_football_score_and_board.json`,
        {
          method: 'POST',
          body: JSON.stringify({
            away_scores: paddedAwayScores,
            home_scores: paddedHomeScores,
            period,
            minutes,
            seconds,
            down,
            distance,
            ball_yardline: ballYardline,
            ball_field_side: ballFieldSide,
            possession,
            away_team_timeouts: awayTimeouts,
            home_team_timeouts: homeTimeouts,
          }),
        },
      );

      if (response.ok) {
        setGame(await response.json());
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 2000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalAwayScore = awayScores.reduce(
    (sum: number, score) => sum + (score || 0),
    0,
  );
  const totalHomeScore = homeScores.reduce(
    (sum: number, score) => sum + (score || 0),
    0,
  );

  if (game === null) {
    return null;
  }

  return (
    <>
      <div className="tray" style={{ marginBottom: 25 }}>
        <div className="inner-tray">
          <GameScorecard game={game} />
        </div>
      </div>
      <div className={classes.controlCenter}>
        {game.last_tracked_at !== null && (
          <div className={classes.lastTracked}>
            Last Tracked:{' '}
            {new Intl.DateTimeFormat('en-US', {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              second: 'numeric',
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }).format(new Date(game.last_tracked_at * 1000))}
          </div>
        )}

        <div className={classes.scoreKeeper}>
          {game.state === 'upcoming' && (
            <button
              className="btn btn-success btn-lg"
              onClick={() => {
                startGame();
              }}
            >
              Start
            </button>
          )}

          {game.state === 'in_progress' && (
            <>
              <div className={classes.inningScoresTable}>
                <table>
                  <thead>
                    <tr>
                      <th>Team</th>
                      {Array.from({ length: maxPeriods }, (_, i) => (
                        <th key={i}>{i + 1}</th>
                      ))}
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={classes.teamLabel}>{game.away_team}</td>
                      {Array.from({ length: maxPeriods }, (_, i) => (
                        <td key={i}>
                          <input
                            type="number"
                            min="0"
                            value={awayScores[i] ?? ''}
                            onChange={(e) =>
                              handleScoreChange(i, 'away', e.target.value)
                            }
                            placeholder="-"
                          />
                        </td>
                      ))}
                      <td className={classes.totalScore}>{totalAwayScore}</td>
                    </tr>
                    <tr>
                      <td className={classes.teamLabel}>{game.home_team}</td>
                      {Array.from({ length: maxPeriods }, (_, i) => (
                        <td key={i}>
                          <input
                            type="number"
                            min="0"
                            value={homeScores[i] ?? ''}
                            onChange={(e) =>
                              handleScoreChange(i, 'home', e.target.value)
                            }
                            placeholder="-"
                          />
                        </td>
                      ))}
                      <td className={classes.totalScore}>{totalHomeScore}</td>
                    </tr>
                  </tbody>
                </table>
                <div className={classes.tableButtons}>
                  <button
                    className={classes.addInningButton}
                    onClick={addExtraPeriod}
                  >
                    + Extra Period
                  </button>
                  <div className={classes.inningControlsInline}>
                    <div className={classes.controlGroup}>
                      <label>Period</label>
                      <input
                        type="number"
                        min="1"
                        value={period}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value, 10);
                          if (!isNaN(newValue) && newValue >= 1) {
                            setPeriod(newValue);
                          }
                        }}
                      />
                    </div>
                    <div className={classes.controlGroup}>
                      <label>Minutes</label>
                      <input
                        type="number"
                        min="0"
                        max="15"
                        value={minutes}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value, 10);
                          if (
                            !isNaN(newValue) &&
                            newValue >= 0 &&
                            newValue <= 59
                          ) {
                            setMinutes(newValue);
                          }
                        }}
                      />
                    </div>
                    <div className={classes.controlGroup}>
                      <label>Seconds</label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={seconds}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value, 10);
                          if (
                            !isNaN(newValue) &&
                            newValue >= 0 &&
                            newValue <= 59
                          ) {
                            setSeconds(newValue);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className={classes.baserunnerControls}>
                  <div
                    style={{
                      display: 'flex',
                      gap: 12,
                      flexWrap: 'wrap',
                      alignItems: 'flex-end',
                    }}
                  >
                    <div className={classes.controlGroup}>
                      <label>Down</label>
                      <input
                        type="number"
                        min="1"
                        max="4"
                        value={down ?? ''}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          if (rawValue === '') {
                            setDown(null);
                            return;
                          }

                          const newValue = parseInt(rawValue, 10);
                          if (
                            !isNaN(newValue) &&
                            newValue >= 1 &&
                            newValue <= 4
                          ) {
                            setDown(newValue);
                          }
                        }}
                      />
                    </div>

                    <div className={classes.controlGroup}>
                      <label>Distance</label>
                      <input
                        type="number"
                        min="0"
                        max="99"
                        value={distance ?? ''}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          if (rawValue === '') {
                            setDistance(null);
                            return;
                          }

                          const newValue = parseInt(rawValue, 10);
                          if (
                            !isNaN(newValue) &&
                            newValue >= 0 &&
                            newValue <= 99
                          ) {
                            setDistance(newValue);
                          }
                        }}
                      />
                    </div>

                    <div className={classes.controlGroup}>
                      <label>Ball Yardline</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={ballYardline ?? ''}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          if (rawValue === '') {
                            setBallYardline(null);
                            return;
                          }

                          const newValue = parseInt(rawValue, 10);
                          if (
                            !isNaN(newValue) &&
                            newValue >= 0 &&
                            newValue <= 100
                          ) {
                            setBallYardline(newValue);
                          }
                        }}
                      />
                    </div>

                    <div className={classes.controlGroup}>
                      <label>Possession</label>
                      <select
                        value={possession}
                        onChange={(e) =>
                          setPossession(e.target.value as GameSide)
                        }
                      >
                        <option value="home">Home</option>
                        <option value="away">Away</option>
                      </select>
                    </div>

                    <div className={classes.controlGroup}>
                      <label>Ball Side</label>
                      <select
                        value={ballFieldSide ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setBallFieldSide(
                            value === '' ? null : (value as GameSide),
                          );
                        }}
                      >
                        <option value="">None</option>
                        <option value="home">Home</option>
                        <option value="away">Away</option>
                      </select>
                    </div>

                    <div className={classes.controlGroup}>
                      <label>Away Timeouts</label>
                      <select
                        value={awayTimeouts}
                        onChange={(e) =>
                          setAwayTimeouts(parseInt(e.target.value, 10))
                        }
                      >
                        <option value="0">0</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                      </select>
                    </div>

                    <div className={classes.controlGroup}>
                      <label>Home Timeouts</label>
                      <select
                        value={homeTimeouts}
                        onChange={(e) =>
                          setHomeTimeouts(parseInt(e.target.value, 10))
                        }
                      >
                        <option value="0">0</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                      </select>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      width: '100%',
                      marginTop: 8,
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      onClick={() => {
                        setDown(1);
                        setDistance(10);
                      }}
                    >
                      Reset to 1st & 10
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => {
                        setDown(null);
                        setDistance(null);
                      }}
                    >
                      Clear Down/Distance
                    </button>
                  </div>
                </div>
              </div>

              <button
                className={`${classes.submitScoresButtonLarge} ${isSuccess ? classes.submitScoresSuccess : ''}`}
                onClick={handleSubmitScores}
                disabled={isSubmitting}
              >
                {isSuccess ? 'Success!' : 'Submit Scores'}
              </button>
            </>
          )}

          {game.state === 'finished' && (
            <>
              <h2 className={classes.teamName}>Game Finished</h2>
              <div className={classes.finalScore}>
                {game.away_team}: {totalAwayScore} vs {game.home_team}:{' '}
                {totalHomeScore}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
